import { GuidePriority } from "../guide";
import type { GuideContext, GuideScript, GuideStep, LevelFacts } from "../guide";

/**
 * Captain Bram's level-1 walkthrough (DESIGN §6.2): a brand-new player comes from the title straight into level 1
 * (main.ts), so the welcome card opens it, after the level's intro card (the guide waits for that on its own). Then
 * the player lays the road, builds two towers and starts the waves
 * with the glove on each card, cell and button, then gets a few words during the first waves. Every step is part of
 * the walkthrough (skipped together, replayed by Practice). The steps are ordered by their facts, not chained with
 * `after`: a player who acts before a line shows (a card tapped under the intro card) must not strand the ones after.
 */

/** The numbered steps' progress pips: [position, count]. */
const PIPS = 9;
/** Sim seconds into wave 1 before "Here they come!". */
const WatchAfterS = 2;
/** Sim seconds into a wave before the speed tip (early enough to land in level 1's first wave, short since its D2 trim). */
const SpeedTipAfterS = 8;
/**
 * The rally flag lesson comes in level 1's second wave, this many sim seconds in: wave 1 already has two lines, and by
 * then the first troops have mustered by the fortress (owner 2026-09-30: tell the player about the flag earlier; it used
 * to wait for level 2 and troops already fighting).
 */
const RallyLessonStage = 2;
const RallyLessonAfterS = 3;

/** The level-1 facts, or null on any other level or screen. */
function l1(c: GuideContext): LevelFacts | null { return c.level !== null && c.level.level === 1 ? c.level : null; }
const W = { screen: "level", walkthrough: true, priority: GuidePriority.Walkthrough } as const;

/** The road draft before anything is picked or laid. */
const roadUntouched = (l: LevelFacts | null) => l !== null && l.phase === "draft" && l.offer === "openingRoad" && l.picked === null && l.roadTiles === 0;
/** The first tower offer, with nothing picked or built. */
const towersUntouched = (l: LevelFacts | null) => l !== null && l.offer === "openingTower" && l.picked === null && l.towersBuilt === 0;

const steps: GuideStep[] = [
  // The first thing Bram says to a new player. Only on a first run (no clears): Practice replays the walkthrough for
  // players who know him, and they skip straight to the fortress line. Its alt link skips the whole walkthrough.
  { ...W, id: "l1.welcome", kind: "welcome", pose: "cheering", priority: GuidePriority.Welcome, holdSim: true,
    title: "WELCOME, COMMANDER!", cta: "TO ARMS!", altCta: "I know the drill",
    text: "I'm **Captain Bram**. The horde marches on the **Highmarch**, so let's raise your first defence together.",
    when: (c) => { const l = l1(c); return l !== null && l.firstClear && c.highestCleared === 0 && l.phase === "draft"; } },
  { ...W, id: "l1.fortress", kind: "info", pose: "neutral", pip: [1, PIPS], topic: "roads",
    target: { world: "base", focus: true }, block: "hard",
    text: "This is our fortress, Commander. The horde wants it. Let's not oblige them.",
    when: (c) => roadUntouched(l1(c)),
    until: (c) => (l1(c)?.picked ?? null) !== null },
  { ...W, id: "l1.pickRoad", kind: "do", pose: "pointing", pip: [2, PIPS], topic: "roads",
    target: { dom: "#hand .cards .card.road", hole: "#hand .cards" }, block: "hard",
    text: "First, we need a road. Tap a **road card**.",
    when: (c) => roadUntouched(l1(c)),
    until: (c) => { const l = l1(c); return l !== null && (l.picked === "road" || l.roadTiles > 0); } },
  { ...W, id: "l1.placeRoad", kind: "do", pose: "pointing", pip: [3, PIPS], topic: "roads",
    target: { dom: "#hand .placement button.accent" }, ring: "ghost", block: "hard",
    text: "The glowing path shows where it goes. Tap **PLACE** to lay it.",
    when: (c) => { const l = l1(c); return l !== null && l.picked === "road" && l.roadTiles === 0; },
    until: (c) => (l1(c)?.roadTiles ?? 0) > 0 },
  { ...W, id: "l1.moreRoad", kind: "do", pose: "cheering", pip: [4, PIPS], topic: "roads",
    block: "none", placement: "top",
    // One line for the whole road: it used to swap to "Tap PLACE…" / "Tap ROTATE…" on every card and back, so the
    // bubble retyped the same two lessons card after card (and "Tap PLACE" repeated l1.placeRoad word for word).
    text: "Good. Keep building: **ROTATE** turns a piece, **PLACE** lays it. More road, more time under our fire.",
    when: (c) => { const l = l1(c); return l !== null && l.offer === "openingRoad" && l.roadTiles >= 1; },
    until: (c) => { const l = l1(c); return l !== null && l.offer !== "openingRoad"; } },
  { ...W, id: "l1.portal", kind: "info", pose: "pointing", pip: [5, PIPS], topic: "roads",
    target: { world: "portal", focus: true }, block: "hard",
    text: "Fine road. The horde pours out of that **portal** at its end. More road cards come as we fight.",
    when: (c) => towersUntouched(l1(c)),
    until: (c) => (l1(c)?.picked ?? null) !== null },
  { ...W, id: "l1.pickTower", kind: "do", pose: "pointing", pip: [6, PIPS], topic: "towers",
    target: { dom: "#hand .cards .card.tower", hole: "#hand .cards" }, block: "hard",
    text: "Now for a tower. Tap a **tower card**.",
    when: (c) => towersUntouched(l1(c)),
    until: (c) => { const l = l1(c); return l !== null && (l.picked === "tower" || l.towersBuilt >= 1); } },
  { ...W, id: "l1.chooseCell", kind: "do", pose: "pointing", pip: [7, PIPS], topic: "towers",
    target: { world: "recommendedTowerCell", radiusCells: 0.7, focus: true }, block: "soft",
    text: "Tap a **green square** by the road. This one's a fine spot.",
    when: (c) => { const l = l1(c); return l !== null && l.picked === "tower" && !l.towerSpotChosen; },
    until: (c) => { const l = l1(c); return l !== null && (l.towerSpotChosen || l.picked !== "tower"); } },
  { ...W, id: "l1.placeTower", kind: "do", pose: "pointing", pip: [8, PIPS], topic: "towers",
    target: { dom: "#hand .placement button.accent" }, ring: "towerSpot", block: "hard",
    text: "The circle is its reach. Tap **PLACE** to raise it.",
    when: (c) => { const l = l1(c); return l !== null && l.picked === "tower" && l.towerSpotChosen && l.towersBuilt === 0; },
    until: (c) => (l1(c)?.towersBuilt ?? 0) >= 1 },
  { ...W, id: "l1.secondTower", kind: "do", pose: "cheering", pip: [8, PIPS], topic: "towers",
    block: "none",
    text: "One more tower. Your call on the spot this time.",
    when: (c) => { const l = l1(c); return l !== null && l.offer === "openingTower" && l.towersBuilt === 1; },
    until: (c) => { const l = l1(c); return l !== null && (l.towersBuilt >= 2 || l.offer !== "openingTower"); } },
  { ...W, id: "l1.start", kind: "do", pose: "pointing", pip: [9, PIPS], topic: "towers",
    target: { dom: "#controls .start" }, block: "hard",
    text: "Ready when you are. Tap **START WAVES**.",
    when: (c) => l1(c)?.phase === "ready",
    until: (c) => { const p = l1(c)?.phase; return p !== undefined && p !== "ready" && p !== "draft"; } },
  { ...W, id: "l1.watch", kind: "tip", pose: "cheering", topic: "towers",
    text: "Here they come! Towers fire on their own, and the fortress joins in.",
    when: (c) => { const l = l1(c); return l !== null && l.phase === "wave" && l.stage === 1 && l.waveSeconds >= WatchAfterS; } },
  { ...W, id: "l1.speed", kind: "tip", pose: "pointing", topic: "speed",
    target: { dom: "#controls .speed" }, block: "none",
    text: "Too slow for you? Tap **×1** to quicken the pace.",
    when: (c) => { const l = l1(c); return l !== null && l.phase === "wave" && l.waveSeconds >= SpeedTipAfterS && l.speed === 1; },
    until: (c) => c.signals.has("speedChanged") || (l1(c)?.speed ?? 1) !== 1 },
  { ...W, id: "l1.points", kind: "info", pose: "pointing", topic: "points",
    target: { dom: "#hud .next" }, block: "hard",
    text: "Every raider we fell earns points. Fill this and you get **new cards**.",
    when: (c) => { const l = l1(c); return l !== null && l.offer === "inRun" && l.picked === null; },
    until: (c) => { const l = l1(c); return l !== null && l.offer !== "inRun"; } },
  { ...W, id: "l1.pickOffer", kind: "do", pose: "pointing", topic: "points", after: ["l1.points"],
    target: { dom: "#hand .cards" }, pointer: false, block: "soft",
    text: (c) => (l1(c)?.noSkipChallenge ?? false) ? "Pick one to add to your defence." : "Pick one, or **SKIP** for bonus points.",
    when: (c) => { const l = l1(c); return l !== null && l.offer === "inRun" && l.picked === null; },
    until: (c) => { const l = l1(c); return l !== null && (l.picked !== null || l.offer !== "inRun"); } },
  { ...W, id: "l1.break", kind: "info", pose: "pointing", topic: "points", holdSim: true,
    target: { dom: "#controls .start" }, block: "hard",
    text: "Wave beaten! The next comes soon, or tap **NEXT WAVE** to call it early for bonus points.",
    when: (c) => { const l = l1(c); return l !== null && l.phase === "break" && l.offer === null; },
    until: (c) => { const l = l1(c); return l !== null && l.phase !== "break"; } },
  // The rally flag, in two moves (the flag only works during a wave, with no cards on the table). Both hold the sim: a
  // considered move, and the wave must not end under the lesson (planting works paused, level/levelInput.ts).
  { ...W, id: "l1.rally", kind: "do", pose: "pointing", topic: "soldiers", holdSim: true, unless: ["hint.rally"],
    target: { dom: "#controls .flag" }, block: "soft",
    text: "Your soldiers wait by the fortress. Tap the **flag**, then the road, to send them where raiders pass.",
    when: (c) => { const l = l1(c); return l !== null && l.phase === "wave" && l.offer === null && l.stage >= RallyLessonStage && l.waveSeconds >= RallyLessonAfterS && !l.rallyArmed; },
    until: (c) => c.signals.has("rallyArmed") || c.signals.has("rallyPlanted") || (l1(c)?.rallyArmed ?? false) },
  { ...W, id: "l1.rallyRoad", kind: "do", pose: "pointing", topic: "soldiers", holdSim: true, after: ["l1.rally"],
    block: "none", placement: "top",
    text: "Now tap the **road** where raiders pass. Your soldiers march there and hold it.",
    when: (c) => l1(c)?.rallyArmed === true,
    until: (c) => c.signals.has("rallyPlanted") || !(l1(c)?.rallyArmed ?? false) },
  { ...W, id: "l1.towerTap", kind: "tip", pose: "pointing", topic: "towers",
    target: { world: "firstTower" }, block: "none",
    text: "Tap any tower to see how it's faring.",
    when: (c) => { const l = l1(c); return l !== null && l.phase === "break" && l.stage >= 2 && l.towersBuilt >= 1; },
    until: (c) => c.signals.has("towerSheetOpened") || (l1(c)?.towerSheetOpen ?? false) },
];

export const level1Script: GuideScript = { id: "level1", steps };

/** The walkthrough's step ids: while one of them speaks, the tray's own hint line keeps quiet (one voice). */
export const LEVEL1_STEP_IDS: ReadonlySet<string> = new Set(steps.map((s) => s.id));
